import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Icon, Thumbnail } from "@culturehq/components";
import { getImageFromStory, isMultiImageStory } from "app/utils";
import imagesSizes from "constants/imagesSizes";

import chatbubble from "images/chatbubble.png";

import CheerList from "components/common/cheers/CheerList";
import LightboxStories from "components/common/gallery/LightboxStories";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import SocialChannel from "components/common/SocialChannel";
import TrendsetterBadge from "./TrendsetterBadge";

const MAX_NUMBER_PHOTOS = 5;

const StoryCreatedThumbnail = ({
  story,
  viewAll,
  index,
  isTrendsetter,
  length,
  onClick,
  isUserProfile,
  showViewAll
}) => {
  const getBackgroundImage = () => {
    if (story.media.mediaType === "video") {
      return `url(${story.media.thumbnail})`;
    }

    if (length === 1) {
      return `url(${getImageFromStory(imagesSizes.NORMAL, story)})`;
    }

    return (index === 0 && (length === 4 || length === 3))
      ? `url(${getImageFromStory(imagesSizes.NORMAL, story)})`
      : `url(${getImageFromStory(imagesSizes.SMALL, story)})`;
  };

  const { creator } = story;

  return (
    <div className="PhotoGroup__thumbnail" onClick={() => onClick(index)} role="button" tabIndex={0}>
      <div className={`${isUserProfile ? "Story__information--profile" : "Story__information--feed"}`}>
        <div className="background-container">
          <LazyEditorOutput output={story.body} showTags={false} preview />
          {isUserProfile && (<span className="Story__question">{story.question.question}</span>)}
          <div className="Story_Badges__Container">
            {isTrendsetter && <TrendsetterBadge justIcon />}
            {story?.socialShares?.map(share => share.socialType).filter(
              (value, index, self) => (self.indexOf(value) === index)
            ).map(type => (
              <SocialChannel key={type} justIcon socialType={type} />
            ))}
          </div>
          {isUserProfile && (
            <div className="Story__actions">
              <div className="Messages__container">
                <img
                  className="Messages__icon"
                  src={chatbubble}
                  alt="Messages badge"
                />
                {story.commentCount > 0 && (<span className="Messages__container--badge">{story.commentCount}</span>)}
              </div>
              <CheerList
                cheersPath={`/stories/${story.id}/cheers`}
                entity={story}
                showCheerButton={false}
                isUserProfile
              />
            </div>
          )}
          {!isUserProfile && (
            <div className="Story__header">
              <div className="Story__creator">
                <Thumbnail image={creator.avatar.thumbUrl} />
                <span>{creator.name}</span>
              </div>
              {story.media.mediaType === "video" && (
                <span className="Story__badge--play">
                  <Icon icon="play-no-border" />
                </span>
              )}
              {isMultiImageStory(story) && (
                <div className="MultiImage__container">
                  <Icon icon="images" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isUserProfile && (
        <div className="Story__information--profile Story__answer">
          <div className="background-container">
            <LazyEditorOutput output={story.body} showTags={false} />
          </div>
        </div>
      )}
      <div
        className="PhotoGroup__wrapper"
        style={{ backgroundImage: getBackgroundImage() }}
      />
      {viewAll && showViewAll && (
        <Link to={`stories/trend/${story.question.id}`} className="PhotoGroup__view-all">
          <Icon icon="images" /> View all
        </Link>
      )}
    </div>
  );
};

const buildStories = (item, stories) => stories
  || item.childUserActivities.concat(item).map(child => child.entity);

class Multiple extends Component {
  constructor(props) {
    super(props);
    const { item, stories } = props;

    this.state = {
      modalIsOpen: false,
      activeStory: null,
      stories: buildStories(item, stories),
      isTrendsetter: false
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { item, stories } = this.props;
    if (item.childUserActivities !== prevProps.item.childUserActivities) {
      this.setState({ stories: buildStories(item, stories) });
    }
  }

  handleThumbnailClick = goToIndex => {
    const { stories } = this.state;
    const { item } = this.props;
    let target = goToIndex;

    if (goToIndex === -1) {
      target = stories.length - 1;
    }

    if (goToIndex > stories.length - 1) {
      target = 0;
    }

    this.setState({
      activeStory: stories[target],
      modalIsOpen: true,
      isTrendsetter: stories[target].id === item?.entity?.id
    });
  };

  handleClose = () => {
    this.setState({ modalIsOpen: false });
  };

  handleStoryUpdate = (updatedStory, updatedUserActivity) => {
    const { onUserActivityUpdate } = this.props;

    if (updatedUserActivity && !!onUserActivityUpdate) {
      onUserActivityUpdate({ userActivity: updatedUserActivity });
      this.setState({ activeStory: updatedStory });
      return;
    }

    this.setState(({ stories }) => ({
      activeStory: updatedStory,
      stories: stories.map(story => (
        story.id === updatedStory.id ? { ...story, ...updatedStory } : story
      ))
    }));
  };

  handleStoryDelete = deleted => {
    const { onDelete } = this.props;
    onDelete(deleted);
    this.setState(state => ({
      stories: state.stories.filter(({ id }) => id !== deleted),
      modalIsOpen: false
    }));
  };

  handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    this.setState(({ stories }) => ({
      stories: stories.map(story => (
        story.id !== storyId ? story : ({ ...story, cheerForUser, cheersCount })
      ))
    }));
  };

  handleCommentsChange = (story, numberOfComments) => {
    this.setState(({ stories }) => ({
      stories: stories.map(storyToUpdate => {
        if (storyToUpdate.id === story.id) {
          return { ...storyToUpdate, commentCount: numberOfComments };
        }
        return storyToUpdate;
      })
    }));
  };

  render() {
    const {
      currentUserAnswered,
      hideLinks,
      isUserProfile,
      item,
      landingPage,
      noActions,
      showViewAll
    } = this.props;
    const { activeStory, isTrendsetter, modalIsOpen, stories } = this.state;

    return (
      <>
        {activeStory && (
          <LightboxStories
            modalIsOpen={modalIsOpen}
            activeStory={activeStory}
            currentUserAnswered={currentUserAnswered}
            stories={stories}
            onCommentsChange={this.handleCommentsChange}
            onClose={this.handleClose}
            onStoryChange={this.handleThumbnailClick}
            onDelete={this.handleStoryDelete}
            onStoryCheerToggle={this.handleStoryCheerToggle}
            onStoryUpdate={this.handleStoryUpdate}
            isTrendsetter={isTrendsetter}
            hideLinks={hideLinks}
            landingPage={landingPage}
            noActions={noActions}
          />
        )}
        <div className="FeedItem__preview__photo_created_multiple" key="preview">
          <div className={`PhotoGroup PhotoGroup-${stories.length >= MAX_NUMBER_PHOTOS ? MAX_NUMBER_PHOTOS : stories.length}`}>
            {stories.slice(0, MAX_NUMBER_PHOTOS).map((story, index) => (
              <StoryCreatedThumbnail
                key={story.id}
                story={story}
                index={index}
                length={stories.length}
                onClick={this.handleThumbnailClick}
                viewAll={index === MAX_NUMBER_PHOTOS - 1}
                isUserProfile={isUserProfile}
                isTrendsetter={story.id === item?.entity?.id}
                showViewAll={showViewAll}
              />
            ))}
          </div>
        </div>
      </>
    );
  }
}

Multiple.defaultProps = {
  showViewAll: true
};

export default Multiple;
